import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import Text from 'modules/core/components/TextField'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileUploader from 'modules/finance/components/FileUploader'
import { useCreateWorkArea } from 'modules/locations/hooks/useCreateWorkArea'
import WorkAreaForm from 'modules/locations/lib/forms/WorkAreaForm'
import { SOURCE } from 'modules/shift_management/hooks/reducers/schedule'
import { useCreateWorkAreaMap } from 'modules/space_control/hooks/useCreateWorkAreaMap'

interface Props {
  onClose: () => void
  locationId: number
}

const AddWorkArea = ({ locationId, onClose }: Props) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const source = queryParams.get('source')
  const comesFromShiftManagement = source === SOURCE
  const viewType = queryParams.get('viewType')

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)

  const workAreaForm = useLazyRef(
    () =>
      new WorkAreaForm({
        name: '',
        location_id: locationId,
      })
  ).current

  const isComplete = !!workAreaForm.get('name').value

  const { createWorkArea } = useCreateWorkArea()
  const { createWorkAreaMap } = useCreateWorkAreaMap()

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await createWorkArea({
      locationId: locationId,
      name: workAreaForm.get('name').value,
    })

    const handledResult = handleResult(result, (errors) => {
      Object.entries(errors).forEach(([field, message]) => {
        workAreaForm.setErrors({ [field]: message })
      })
    })

    if (handledResult.success && handledResult.data.workArea?.id && selectedFile) {
      const mapResult = await createWorkAreaMap({
        workAreaId: handledResult.data.workArea.id,
        image: selectedFile,
      })

      const handledMapResult = handleResult(mapResult, () => {})

      if (!handledMapResult.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.locations.location.work_areas.map.save_map_error_message'),
        })
      }
    }

    if (comesFromShiftManagement) {
      track(trackingEvents.workAreas.addWorkAreaSubmit, {
        view: viewType,
        workplace: locationId,
      })
    }

    track(trackingEvents.spaceControl.publishWorkArea, workAreaForm.values)

    closePortal()
  }

  const modalTitle = i18n.t('settings.locations.location.work_areas.add.title')
  const modalDescription = i18n.t('settings.locations.location.work_areas.add.description')
  const modalButton = i18n.t('settings.locations.location.work_areas.add.button')

  return (
    <Modal
      illustration="calendar"
      title={modalTitle}
      description={modalDescription}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button disabled={!isComplete} submit>
          {modalButton}
        </Button>
      }
    >
      <Box gap="s16">
        <Text
          label={i18n.t('settings.locations.location.work_areas.field_name')}
          value={workAreaForm.get('name').value}
          onChange={(value: string) => workAreaForm.setValues({ name: value })}
        />
        <FileUploader
          placeholder={i18n.t('settings.locations.location.work_areas.map.image_placeholder')}
          description=""
          selectedFile={selectedFile}
          onDrop={(files: File[]) => setSelectedFile(files[0])}
          acceptedFormats={{ 'image/*': [] }}
        />
      </Box>
    </Modal>
  )
}

export default observer(AddWorkArea)
